import { useEffect, useState } from "react";
import { api, saveAdminToken, getAdminToken } from "@/api";
import PageHero from "@/components/tex/PageHero";

const TABS = [
  { key: "quotes", label: "Quote requests", loader: () => api.adminQuotes(), listKey: "requests", empty: "No quote requests yet." },
  { key: "contacts", label: "Contact messages", loader: () => api.adminContacts(), listKey: "messages", empty: "No contact messages yet." },
  { key: "incidents", label: "Incident logs", loader: () => api.adminIncidents(), listKey: "logs", empty: "No incidents logged." },
];

function LoginCard({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | error
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const data = await api.login(email.trim().toLowerCase(), password);
      saveAdminToken(data.access_token);
      onLoggedIn(data.user);
    } catch (err) {
      setStatus("error");
      setError(err.message || "Login failed.");
    }
  }

  return (
    <div className="wrap" style={{ padding: "48px 0" }}>
      <div
        className="card"
        style={{ maxWidth: 420, margin: "0 auto", padding: 28 }}
        data-testid="admin-login-card"
      >
        <h2 style={{ marginTop: 0 }}>Admin sign-in</h2>
        <p className="note" style={{ marginBottom: 20 }}>
          Restricted area — Texcheme sales &amp; safety team only.
        </p>
        <form onSubmit={submit} className="stack">
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="admin-email-input"
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="admin-password-input"
            />
          </label>
          {error && (
            <p className="note" style={{ color: "#c62828" }} data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            className="btn btn-brand"
            type="submit"
            disabled={status === "sending"}
            data-testid="admin-login-submit"
          >
            {status === "sending" ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function DataTable({ rows, columns }) {
  if (!rows.length) return null;
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="admin-table" data-testid="admin-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i}>
              {columns.map((c) => (
                <td key={c.key}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const COLUMNS = {
  quotes: [
    { key: "id", label: "Ref" },
    { key: "received_at", label: "When", render: (r) => new Date(r.received_at).toLocaleString() },
    { key: "name", label: "Name" },
    { key: "company", label: "Company" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "chemical_name", label: "Chemical" },
    { key: "quantity", label: "Qty" },
    { key: "source", label: "Source" },
  ],
  contacts: [
    { key: "id", label: "Ref" },
    { key: "received_at", label: "When", render: (r) => new Date(r.received_at).toLocaleString() },
    { key: "name", label: "Name" },
    { key: "company", label: "Company" },
    { key: "email", label: "Email" },
    { key: "subject", label: "Subject" },
    { key: "message", label: "Message" },
  ],
  incidents: [
    { key: "id", label: "Ref" },
    { key: "received_at", label: "When", render: (r) => new Date(r.received_at).toLocaleString() },
    { key: "chemical_name", label: "Chemical" },
    { key: "volume_l", label: "Vol (L)" },
    { key: "classification", label: "Class" },
    { key: "location", label: "Location" },
    { key: "reporter_name", label: "Reporter" },
    { key: "action_taken", label: "Action" },
  ],
};

function AdminConsole({ user, onSignOut }) {
  const [tab, setTab] = useState("quotes");
  const [data, setData] = useState({ quotes: null, contacts: null, incidents: null });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const tabDef = TABS.find((t) => t.key === tab);

  async function load(key) {
    setLoading(true);
    setError("");
    try {
      const t = TABS.find((tt) => tt.key === key);
      const payload = await t.loader();
      setData((d) => ({ ...d, [key]: payload[t.listKey] || [] }));
    } catch (err) {
      if (err.status === 401 || err.status === 403) {
        saveAdminToken(null);
        onSignOut();
        return;
      }
      setError(err.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (data[tab] === null) load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const rows = data[tab] || [];

  return (
    <div className="wrap" style={{ padding: "32px 0" }}>
      <div
        style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 12 }}
        data-testid="admin-header"
      >
        <div>
          <h2 style={{ marginBottom: 4 }}>Safety &amp; sales inbox</h2>
          <p className="note" style={{ margin: 0 }}>
            Signed in as <strong>{user.email}</strong>
          </p>
        </div>
        <button
          className="btn btn-sm"
          onClick={() => {
            saveAdminToken(null);
            onSignOut();
          }}
          data-testid="admin-signout"
          style={{ background: "#eef2f7", color: "#0f2748", border: "1px solid #d5dde8" }}
        >
          Sign out
        </button>
      </div>

      <div className="tabs" style={{ margin: "24px 0" }} data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={tab === t.key ? "on" : ""}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
          >
            {t.label}
            {data[t.key] ? <span className="badge" style={{ marginLeft: 8 }}>{data[t.key].length}</span> : null}
          </button>
        ))}
      </div>

      {loading && <p className="note" data-testid="admin-loading">Loading…</p>}
      {error && (
        <p className="note" style={{ color: "#c62828" }} data-testid="admin-error">
          {error}
        </p>
      )}
      {!loading && !rows.length && !error && (
        <p className="note" data-testid="admin-empty">{tabDef.empty}</p>
      )}
      {rows.length > 0 && <DataTable rows={rows} columns={COLUMNS[tab]} />}
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    document.title = "Admin — Texcheme";
    window.scrollTo(0, 0);
    if (!getAdminToken()) {
      setChecking(false);
      return;
    }
    api
      .me()
      .then((u) => setUser(u))
      .catch(() => saveAdminToken(null))
      .finally(() => setChecking(false));
  }, []);

  return (
    <div data-testid="admin-page">
      <PageHero
        eyebrow="Internal"
        title="Admin console"
        subtitle="Quote leads, contact messages and spill incident logs."
      />
      {checking ? (
        <div className="wrap" style={{ padding: "48px 0" }}>
          <p className="note">Loading…</p>
        </div>
      ) : user ? (
        <AdminConsole user={user} onSignOut={() => setUser(null)} />
      ) : (
        <LoginCard onLoggedIn={setUser} />
      )}
    </div>
  );
}
